/**
 * Episode plan: frequency orders → per-discipline tracks on SOC-anchored 7-day
 * weeks across the certification period, front-loaded within each week and
 * within each 30-day payment period. Enrichment order is the POC (orders always
 * win) → protocols → starter defaults; this module only turns orders into visits.
 *
 * Plans arrive one discipline at a time: `expandPlan` builds the tracks known at
 * the SOC; `expandTrack` adds a discipline whose orders landed later, anchored on
 * the day that discipline evaluated (staggered instantiation), and stepping over
 * the weekend so a late track never plots a Saturday. A new plan version never
 * rebuilds: delivered, missed and real visits from the previous version are
 * kept; only future placeholders are replaced.
 */
import type { ConfigState, DateKey, Discipline, Episode, FrequencyOrder, PendingDiscipline, PlanVersion, PlanVisit, VisitTypeKind } from "../state";
import { addDaysToKey, addWorkingDaysToKey, dateKeyAt, daysBetweenKeys, isoWeekday, isWorkingDayKey } from "./clock";
import { frontLoadedWeeklyCounts, parseFrequency } from "./frequency";
import { primaryRoutineKind } from "./visitTypes";

/**
 * Day offsets inside a 7-day week for n visits, biased toward the front of the
 * week. Two a week sits on days 1 and 3 so a Monday SOC reads Tuesday / Thursday.
 */
export const WEEKDAY_OFFSETS: Record<number, number[]> = {
  0: [],
  1: [1],
  2: [1, 3],
  3: [0, 2, 4],
  4: [0, 1, 3, 4],
  5: [0, 1, 2, 3, 4],
  6: [0, 1, 2, 3, 4, 5],
  7: [0, 1, 2, 3, 4, 5, 6],
};

export function offsetsFor(perWeek: number): number[] {
  if (perWeek <= 7) return WEEKDAY_OFFSETS[perWeek];
  return Array.from({ length: 7 }, (_, i) => i);
}

export function evalKindFor(discipline: Discipline): VisitTypeKind | undefined {
  switch (discipline) {
    case "PT":
      return "PT_EVAL";
    case "OT":
      return "OT_EVAL";
    case "SLP":
      return "SLP_EVAL";
    default:
      return undefined;
  }
}

export function certEndDate(socDate: DateKey, config: Pick<ConfigState, "certPeriodDays">): DateKey {
  return addDaysToKey(socDate, config.certPeriodDays - 1);
}

export function weekIndexFor(episode: Pick<Episode, "socDate">, date: DateKey): number {
  return Math.floor(daysBetweenKeys(episode.socDate, date) / 7);
}

export function paymentPeriodFor(episode: Pick<Episode, "socDate">, config: Pick<ConfigState, "paymentPeriodDays">, date: DateKey): number {
  return Math.floor(daysBetweenKeys(episode.socDate, date) / config.paymentPeriodDays);
}

export interface ExpandOptions {
  /** The SOC path discipline; its SOC visit is the anchor and counts as week 0's first visit. */
  socDiscipline: Discipline;
  socRequiredTraits?: string[];
  /** ISO weekdays the patient can be seen, as the branch knew when booking; visits keep to them. */
  availableWeekdays?: number[];
}

function visitId(episodeId: string, version: number, discipline: string, index: number): string {
  return `${episodeId}:v${version}:${discipline}:${index}`;
}

interface TrackOptions {
  /** Days the track must not land on (the SOC, an eval already delivered); a colliding visit moves to the next free day. */
  reservedDates?: DateKey[];
  /** Emit the discipline's evaluation on `anchorDate + 1` (true at the SOC; false when the eval was already delivered). */
  withEval: boolean;
  /** Week index reference: the episode's SOC (so tracks anchored later still read on episode weeks). */
  anchorDate: DateKey;
  /** ISO weekdays the patient can be seen; absent, any working day. */
  availableWeekdays?: number[];
}

/**
 * **The days a week's visits fall on: working days inside the week.** A plan
 * week starts on the start of care's weekday, so its seven days always hold
 * five working days; the week's visits take them front-loaded, in the same
 * pattern a Monday week always had (two a week on the second and fourth working
 * days: Tuesday and Thursday from a Monday). A day the track must keep clear —
 * the start of care, an evaluation — gives way to the next free working day in
 * the same week. Only a week asking for more visits than it has working days
 * reaches into its weekend. So a visit never sits on a Saturday and never spills
 * into the next plan week, whichever weekday the episode began on. Days after
 * `lastDay` (the certification's end) and weekdays the patient cannot be seen
 * are not in the week at all.
 */
export function daysInWeek(weekStart: DateKey, count: number, reserved: ReadonlySet<DateKey>, lastDay?: DateKey, availableWeekdays?: number[]): DateKey[] {
  const week = Array.from({ length: 7 }, (_, i) => addDaysToKey(weekStart, i)).filter((d) => (!lastDay || d <= lastDay) && (!availableWeekdays || availableWeekdays.includes(isoWeekday(d))));
  const working = week.filter(isWorkingDayKey);
  const weekend = week.filter((d) => !isWorkingDayKey(d));
  const out: DateKey[] = [];
  const free = (d: DateKey) => !reserved.has(d) && !out.includes(d);
  for (const index of offsetsFor(Math.min(count, working.length))) {
    const preferred = working[Math.min(index, working.length - 1)];
    const pick = free(preferred) ? preferred : [...working.slice(working.indexOf(preferred)), ...working, ...weekend].find(free);
    if (pick) out.push(pick);
  }
  for (const d of weekend) if (out.length < count && free(d)) out.push(d);
  return out.sort();
}

/** One discipline's visits from its order, anchored on `anchorDate`, on episode-relative week indexes. */
function expandOrderTrack(episode: Episode, version: number, order: FrequencyOrder, config: ConfigState, opts: TrackOptions): PlanVisit[] {
  const out: PlanVisit[] = [];
  const parsed = parseFrequency(order.pattern);
  const routineKind = primaryRoutineKind(order.discipline);
  const evalKind = evalKindFor(order.discipline);
  const traits = order.requiredTraits ?? [];
  const certEnd = episode.certEndDate;
  const reserved = new Set(opts.reservedDates ?? []);
  let counter = 0;
  const next = () => ++counter;
  const anchored = opts.anchorDate !== episode.socDate;

  // Therapy tracks open with an evaluation inside the first week: the next working day after the anchor, never SOC day.
  if (evalKind && opts.withEval) {
    const evalDate = addWorkingDaysToKey(opts.anchorDate, 1);
    if (evalDate <= certEnd) {
      out.push({
        id: visitId(episode.id, version, order.discipline, next()),
        episodeId: episode.id,
        discipline: order.discipline,
        visitTypeKind: evalKind,
        weekIndex: weekIndexFor(episode, evalDate),
        targetDate: evalDate,
        requiredTraits: traits,
        status: "placeholder",
        planVersion: version,
        anchorDate: anchored ? opts.anchorDate : undefined,
      });
      reserved.add(evalDate);
    }
  }
  if (parsed.evalOnly) return out;

  let weekCursor = 0;
  for (const segment of parsed.segments) {
    const weekly = frontLoadedWeeklyCounts(segment);
    for (const count of weekly) {
      const weekStart = addDaysToKey(opts.anchorDate, weekCursor * 7);
      for (const targetDate of daysInWeek(weekStart, count, reserved, certEnd, opts.availableWeekdays)) {
        out.push({
          id: visitId(episode.id, version, order.discipline, next()),
          episodeId: episode.id,
          discipline: order.discipline,
          visitTypeKind: routineKind,
          weekIndex: weekIndexFor(episode, targetDate),
          targetDate,
          requiredTraits: traits,
          status: "placeholder",
          planVersion: version,
          anchorDate: anchored ? opts.anchorDate : undefined,
        });
      }
      weekCursor += 1;
    }
  }
  return out;
}

function sortVisits(visits: PlanVisit[]): PlanVisit[] {
  return visits.sort((a, b) => a.targetDate.localeCompare(b.targetDate) || a.discipline.localeCompare(b.discipline));
}

/** Expand one plan version into plan visits (all placeholders; statuses are applied later). */
export function expandPlan(episode: Episode, version: PlanVersion, config: ConfigState, opts: ExpandOptions): PlanVisit[] {
  const out: PlanVisit[] = [];

  // The SOC assessment anchors the episode.
  out.push({
    id: visitId(episode.id, version.version, "SOC", 0),
    episodeId: episode.id,
    discipline: opts.socDiscipline,
    visitTypeKind: "SOC",
    weekIndex: 0,
    targetDate: episode.socDate,
    requiredTraits: [...config.visitTypes.SOC.requiredTraits, ...(opts.socRequiredTraits ?? [])],
    status: "placeholder",
    planVersion: version.version,
  });

  for (const order of version.orders) {
    out.push(...expandOrderTrack(episode, version.version, order, config, { anchorDate: episode.socDate, withEval: true, reservedDates: [episode.socDate], availableWeekdays: opts.availableWeekdays }));
  }
  return sortVisits(out);
}

export interface ExpandTrackOptions {
  /** The discipline's evaluation was already delivered; start with the routine visits. */
  evalDelivered?: boolean;
  /** ISO weekdays the patient can be seen, from the welcome call; the track's days stay on them. */
  availableWeekdays?: number[];
}

/**
 * A discipline whose orders arrive after the SOC (therapy evaluates, then plans):
 * its track is anchored on the day the orders landed, on the episode's week indexes.
 */
export function expandTrack(episode: Episode, order: FrequencyOrder, anchorDate: DateKey, version: number, config: ConfigState, opts: ExpandTrackOptions = {}): PlanVisit[] {
  return sortVisits(expandOrderTrack(episode, version, order, config, { anchorDate, withEval: !opts.evalDelivered, reservedDates: [episode.socDate], availableWeekdays: opts.availableWeekdays }));
}

export function startVersion(previous: PlanVersion | undefined, orders: FrequencyOrder[], createdAtIso: string, reason: string): PlanVersion {
  return { version: (previous?.version ?? 0) + 1, createdAtIso, reason, orders };
}

/** What a patient's episode plan is built from: the same inputs for the store and the seed. */
export interface PlanSubject {
  ref: string;
  socDate: DateKey;
  caseType: string;
  /** Orders the cast states; they win over the case type's starter defaults. */
  orders?: FrequencyOrder[];
  /** Traits the case needs; they ride on the nursing orders. */
  requiredTraits?: string[];
  pendingDisciplines?: PendingDiscipline[];
  /** ISO weekdays the patient can be seen, as the branch knew when booking. */
  availableWeekdays?: number[];
}

/**
 * **One plan per patient, for the store and the seed alike.** The episode, its
 * first plan version and the expanded visits — before any rule (supervisory,
 * recert) or stage is applied, because those are the store's. The seed places
 * these visits on the tenant and the store binds to them, so both sides must
 * expand the same orders on the same start of care under the same episode id;
 * plan-visit ids embed it.
 */
export function expandEpisode(subject: PlanSubject, config: ConfigState): { episode: Episode; version: PlanVersion; visits: PlanVisit[] } {
  const source = subject.orders ?? config.starterFrequencies[subject.caseType] ?? [];
  const orders = source.map((o) => ({ ...o, requiredTraits: o.discipline === "RN" ? subject.requiredTraits ?? o.requiredTraits : o.requiredTraits }));
  const version = startVersion(undefined, orders, dateKeyAt(addDaysToKey(subject.socDate, 1), "16:00", config.branch.timeZone).toISOString(), "Frequency orders synced from the EHR");
  const episode: Episode = {
    id: `ep-${subject.ref}`,
    patientRef: subject.ref,
    socDate: subject.socDate,
    certEndDate: certEndDate(subject.socDate, config),
    versions: [version],
    authLedger: [],
    lupaThreshold: config.lupaThresholdPerPeriod,
    pendingDisciplines: subject.pendingDisciplines ?? [],
  };
  const visits = expandPlan(episode, version, config, { socDiscipline: "RN", socRequiredTraits: subject.requiredTraits ?? [], availableWeekdays: subject.availableWeekdays });
  return { episode, version, visits };
}
